import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CorporateSubscriptionInvoiceGroupDetail,
  FacilitySubscriptionResponse,
  FacilitySubscriptionStatus,
  FacilityType,
  InvoiceStatus,
  OpenCycleSubscription,
  PagedResultDto,
  SubscriptionInvoice,
  SubscriptionInvoiceListRow,
  toSubscriptionProducts,
  warnOnTierPriceDrift
} from '@umss/shared/data-access';
import { HttpHelper } from '@umss/shared/utils';
import {
  catchError,
  map,
  Observable,
  of,
  OperatorFunction,
  tap,
  throwError
} from 'rxjs';

/**
 * Filters for the cross-facility invoice list.
 *
 * `facilityTypeId` is a lookup-table id, not an enum member the API knows by
 * name — `FacilityType` is the frontend's mirror of that table, so its numeric
 * values are what go over the wire.
 *
 * There is no sort parameter: the API fixes the order at newest cycle first,
 * matching the per-facility list, and the page has no sort control.
 */
export interface SubscriptionInvoiceQuery {
  searchTerm?: string;
  facilityTypeId?: FacilityType;
  status?: InvoiceStatus;
  pageNumber?: number;
  pageSize?: number;
}

/**
 * A 404 from the subscription routes means "this facility has never
 * subscribed" — the routine free-tier case, not a failure.
 *
 * The status is overloaded: an unknown facility id answers the same way. So a
 * 404 says only "no subscription to show", never that the facility is absent.
 */
function neverSubscribed<T, F>(fallback: F): OperatorFunction<T, T | F> {
  return catchError<T, Observable<F>>((error: unknown) =>
    error instanceof HttpErrorResponse && error.status === 404
      ? of(fallback)
      : throwError(() => error)
  );
}

/**
 * One facility's subscription, read from the business app.
 *
 * **Reads only, and that is the design.** A business administrator is not the
 * payer: a facility subscribes itself, or its corporation does on its behalf.
 * So the business app reports a subscription and never changes one — the same
 * position a corporate-billed facility is in when it looks at its own.
 *
 * The per-facility reads reuse the facility routes rather than an admin
 * parallel, because there is no admin parallel:
 * `FacilityAccessAuthorizationHandler` already grants BusinessAdministrator
 * access to every facility, so those GETs work as they stand for any facility
 * id. Only the cross-facility list needs a route of its own — no facility id
 * exists to nest it under.
 *
 * The mutation routes would *not*. They are gated on the
 * `CanManageSubscriptions` policy, and `manage_subscriptions` is absent from
 * the BusinessAdministrator role defaults — so adding a write here needs a
 * backend change first, not just a method.
 */
@Injectable({ providedIn: 'root' })
export class BusinessSubscriptionService {
  private readonly http = inject(HttpClient);

  private baseUrl(facilityId: string): string {
    return `api/facilities/${facilityId}/subscription`;
  }

  /**
   * Subscription invoices across every facility the caller may see.
   *
   * Nested under `api/facilities` rather than given a first path segment of
   * its own: a new top-level segment needs a manual AWS WAF allowlist edit
   * before it stops returning 403 in every environment.
   *
   * Rows carry totals and the tier snapshot but no lines — the detail route
   * is where a breakdown comes from.
   *
   * No `neverSubscribed` here, deliberately. A caller with nothing to show
   * gets an empty page, not a 404, so a 404 from this route would be a real
   * failure and must surface as one.
   */
  getInvoices(
    query: SubscriptionInvoiceQuery = {}
  ): Observable<PagedResultDto<SubscriptionInvoice>> {
    return this.http.get<PagedResultDto<SubscriptionInvoice>>(
      'api/facilities/subscription-invoices',
      { params: HttpHelper.toHttpParams(query) }
    );
  }

  /**
   * The same list with corporate-billed invoices collapsed into one row per
   * corporation per cycle.
   *
   * Takes the same query as {@link getInvoices} and scopes identically. Two
   * behaviours of the API shape what the page can show:
   *
   * - Setting `facilityTypeId` **drops the group half** and reveals those
   *   invoices individually. A group spans facility types and so has no
   *   honest answer to the filter; degrading to the flat list is lossless,
   *   where matching "any member" would return a row whose breakdown contains
   *   facilities the filter excluded.
   * - A group can match **more than one** status. `Paid` tests the group's own
   *   aggregate ("all members settled"), every other value tests whether a
   *   member is in that state — so a group holding one invoiced and one past
   *   due invoice appears under both. The filter does not partition the list,
   *   and no count may be derived from it.
   */
  getGroupedInvoices(
    query: SubscriptionInvoiceQuery = {}
  ): Observable<PagedResultDto<SubscriptionInvoiceListRow>> {
    return this.http.get<PagedResultDto<SubscriptionInvoiceListRow>>(
      'api/facilities/subscription-invoices/grouped',
      { params: HttpHelper.toHttpParams(query) }
    );
  }

  /**
   * One corporation's invoice group for a cycle, with its member invoices.
   *
   * Reuses the corporate route rather than a business parallel, because there
   * is no business parallel and none is needed: `ValidateCorporateAccess`
   * already lets a BusinessAdministrator with no `corporate_id` through for
   * any corporation, and the route is gated on `CanViewInvoices`, which the
   * role holds.
   *
   * Read-only here. The corporate portal owns paying a group; a business
   * administrator is not the payer.
   */
  getInvoiceGroup(
    corporationId: string,
    groupId: string
  ): Observable<CorporateSubscriptionInvoiceGroupDetail> {
    return this.http.get<CorporateSubscriptionInvoiceGroupDetail>(
      `api/corporate/${corporationId}/invoice-groups/${groupId}`
    );
  }

  /**
   * Active state of every subscription product for the facility.
   *
   * Read off the subscription record rather than a dedicated route. A facility
   * that has never subscribed resolves to no products — the free-tier state.
   */
  getProducts(facilityId: string): Observable<FacilitySubscriptionStatus[]> {
    return this.http
      .get<FacilitySubscriptionResponse>(this.baseUrl(facilityId))
      .pipe(
        map(toSubscriptionProducts),
        neverSubscribed<
          FacilitySubscriptionStatus[],
          FacilitySubscriptionStatus[]
        >([])
      );
  }

  /**
   * One subscription invoice with its line items.
   *
   * Needs the facility id as well as the invoice id — every subscription route
   * is nested under a facility, so the invoice id alone cannot address it.
   * That is why the detail route carries both.
   *
   * No 404 handling here, deliberately: unlike the routes above, a missing
   * invoice is a genuine error rather than the free-tier case.
   */
  getInvoice(
    facilityId: string,
    invoiceId: string
  ): Observable<SubscriptionInvoice> {
    return this.http.get<SubscriptionInvoice>(
      `${this.baseUrl(facilityId)}/invoices/${invoiceId}`
    );
  }

  /**
   * The open cycle, as a live tax-exclusive estimate, for the usage widget.
   *
   * Null where there is no active subscription or no current cycle.
   */
  getOpenCycle(facilityId: string): Observable<OpenCycleSubscription | null> {
    return this.http
      .get<OpenCycleSubscription>(`${this.baseUrl(facilityId)}/open-cycle`)
      .pipe(
        // The apps display their own ladder; this is the only place the API's
        // copy is visible, so it is checked rather than ignored.
        tap((cycle) => warnOnTierPriceDrift(cycle.bands)),
        neverSubscribed<OpenCycleSubscription, null>(null)
      );
  }
}
